import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,
} from "@mui/material";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import useHttp from "../../Hooks/use-http";
import { useHistory } from "react-router-dom";
import Loading from "../../Loading/Loading";
import SearchFilter from "../CommonComponent/SearchFilter";
import CommonTablePagination from "../CommonComponent/CommonTablePagination";
import VerifiedIcon from "@mui/icons-material/Verified";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import FuelExportComponent from "./FuelExportComponent";
import Modal from "../../GeneratePDF/Modal";
import PortalTitle from "../CommonComponent/PortalTitle";
import Button from "../CommonComponent/Button";
import { PAGINATIONBASEVALUE } from "../../Constant";

// Updated headers structure with both label and key
const headers = [
  { label: "Reg. Number", key: "regNumber" },
  { label: "Odometer", key: "latestOdometer" },
  { label: "Color", key: "color" },
  { label: "Model", key: "model" },
  { label: "Maker", key: "maker" },
  { label: "Fuel Type", key: "fuelType" },
  { label: "Seats", key: "seats" },
  { label: "Vehicle Type", key: "vehicleType" },
  { label: "Status", key: "flag" },
  { label: "Action", key: "action" },
];

const headerWidths = {
  "Reg. Number": "15%",
  Color: "10%",
  Model: "15%",
  Maker: "15%",
  "Fuel Type": "10%",
  Seats: "10%",
  "Vehicle Type": "10%",
  Status: "10%",
  Action: "10%",
};

// Helper function for stable sorting
function stableSort(array, comparator) {
  if (!array) return [];
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function descendingComparator(a, b, orderBy) {
  const valueA = getOrderValue(a, orderBy);
  const valueB = getOrderValue(b, orderBy);

  if (valueB < valueA) return -1;
  if (valueB > valueA) return 1;
  return 0;
}

function getOrderValue(item, key) {
  // Handle status field specially
  if (key === "flag") {
    return item.flag ? "Approved" : "Not Approved";
  }
  const value = item[key] || "";
  return typeof value === "string" ? value.toLowerCase() : value;
}

const Vehicles = () => {
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("regNumber");
  const [vehicleData, setVehicleData] = useState([]);
  const { sendRequest, isLoading } = useHttp();
  const history = useHistory();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGINATIONBASEVALUE);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [filteredData, setFilteredData] = useState(null);
  const [search, setSearch] = useState("");
  const [isApiCall, setIsApiCall] = useState(false);
  const [isExportButtonClicked, setIsExportButtonClicked] = useState(false);
  const [isVehicleReportClicked, setIsVehicleReportClicked] = useState(false);

  useEffect(() => {
    sendRequest(
      {
        url: "/vehicles",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: {
          emailID: localStorage.getItem("user"),
        },
      },
      vehicleList
    );
  }, [sendRequest, isApiCall]);

  const vehicleList = (data) => {
    setVehicleData(data?.vehicles || []);
  };

  useEffect(() => {
    const sortedData = stableSort(
      vehicleData,
      order === "asc"
        ? (a, b) => ascendingComparator(a, b, orderBy)
        : (a, b) => descendingComparator(a, b, orderBy)
    );

    const paginatedData = sortedData?.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

    setFilteredData(paginatedData);
  }, [vehicleData, order, orderBy, page, rowsPerPage]);

  function ascendingComparator(a, b, orderBy) {
    const valueA = getOrderValue(a, orderBy);
    const valueB = getOrderValue(b, orderBy);

    if (valueA < valueB) return -1;
    if (valueA > valueB) return 1;
    return 0;
  }

  const handleSort = (columnKey) => {
    const isAsc = orderBy === columnKey && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnKey);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuClick = (event, vehicle) => {
    setAnchorEl(event.currentTarget);
    setSelectedVehicle(vehicle);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedVehicle(null);
  };

  const handleVerificationDocument = (item) => {
    history.push({
      pathname: "/verification-documents-vehicle",
      state: { data: item },
    });
  };

  const handleApproval = async (row) => {
    const updatedStatus = row?.flag == 1 ? false : true;

    sendRequest(
      {
        url: "/vehicle-approve",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: {
          emailID: localStorage.getItem("user"),
          data: {
            numberPlate: row.regNumber,
            flag: updatedStatus,
          },
        },
      },
      (response) => {
        let parsedResponse;
        try {
          if (typeof response === "string") {
            const jsonStartIndex = response.indexOf("{");
            if (jsonStartIndex !== -1) {
              parsedResponse = JSON.parse(response.substring(jsonStartIndex));
            } else {
              parsedResponse = { message: "Unexpected response format" };
            }
          } else if (typeof response === "object") {
            parsedResponse = response;
          } else {
            parsedResponse = { message: "Unexpected response format" };
          }
        } catch (error) {
          parsedResponse = { message: "Unexpected response format" };
        }

        if (parsedResponse.status === 200 || parsedResponse.status === 201) {
          toast.success(
            parsedResponse.message ||
              `Driver ${updatedStatus ? "approved" : "rejected"} successfully!`
          );
          row.flag = updatedStatus;
          setAnchorEl(false);
          setIsApiCall((prev) => !prev);
        } else {
          console.log("❌ Error Response:", parsedResponse);
          toast.error(parsedResponse.message || "Unexpected error occurred.");
        }
      }
    );
  };

  const handleFuelClick = (row) => {
    history.push({
      pathname: "/fuels",
      state: { data: row.regNumber },
    });
  };

  return (
    <React.Fragment>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Paper
          sx={{
            width: "100%",
            mb: 2,
            borderRadius: "10px",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            border: "none",
            borderBottom: "none",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              pt: 1,
              pb: 1,
              borderRadius: "10px",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 1, md: 0 },
            }}
          >
            <Box component="h2" sx={{ m: 0, display: "flex", alignItems: "center" }}>
              <PortalTitle fallback="Vehicles" />
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: "12px",
                alignItems: "center",
                flexDirection: { xs: "column", md: "row" },
            }}
          >
            <SearchFilter
              data={vehicleData}
              setSearchValue={setSearch}
              searchValue={search}
              setFilteredData={setFilteredData}
            />
              <Button
                label="Export"
                variant="outlined"
                backgroundColor="#1D85C6"
                textColor="#1D85C6"
                sx={{
                  color: "#1D85C6",
                  border: "1px solid #1D85C6",
                  ":hover": {
                    border: "1px solid #1D85C6",
                    color: "black",
                  },
                }}
                size="small"
                onClick={() => setIsVehicleReportClicked(true)}
              />
              <Button
                label="Add Vehicle"
                onClick={() => history.push("/new-vehicle")}
                variant="contained"
                size="medium"
                sx={{
                  backgroundColor: "#44B150",
                  ":hover": {
                    backgroundColor: "#44B150",
                    opacity: 0.8,
                  },
                  boxShadow: "none",
                  fontFamily: "Montserrat",
                  fontSize: "12px",
                  padding: "8px 20px",
                  textTransform: "none",
                  minWidth: "110px",
                  marginLeft: "12px",
                }}
              />
            </Box>
          </Box>
          <TableContainer
            component={Paper}
            sx={{
              width: "100%",
              margin: "auto",
              borderRadius: "10px",
              boxShadow: "none",
              border: "none",
              borderBottom: "none",
              "& .MuiPaper-root": {
                border: "none",
                borderBottom: "none",
              },
            }}
          >
            <Table
              sx={{
                fontFamily: "Montserrat",
              }}
              size="medium"
            >
              <TableHead>
                <TableRow>
                  {headers?.map((header) => (
                    <TableCell
                      key={header.key}
                      align="center"
                      sx={{
                        py: 1,
                        px: 2,
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        color: "rgba(0, 0, 0, 0.87)",
                        width: headerWidths[header.label],
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === header.key}
                        direction={orderBy === header.key ? order : "asc"}
                        onClick={() => handleSort(header.key)}
                        sx={{ fontSize: "0.875rem" }}
                      >
                        {header.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={headers?.length}>
                      <Loading datatable="true" />
                    </TableCell>
                  </TableRow>
                ) : filteredData?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={headers?.length}>
                      <div style={{ textAlign: "center", marginTop: "10px" }}>
                        No Data Available
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData?.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell
                        align="center"
                        sx={{ fontSize: "0.875rem", py: 1, px: 2 }}
                      >
                        {item.regNumber || "-"}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontSize: "0.875rem", py: 1, px: 2 }}
                      >
                        {item.latestOdometer || "-"}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontSize: "0.875rem", py: 1, px: 2 }}
                      >
                        {item.color || "-"}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontSize: "0.875rem", py: 1, px: 2 }}
                      >
                        {item.model || "-"}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontSize: "0.875rem", py: 1, px: 2 }}
                      >
                        {item.maker || "-"}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontSize: "0.875rem", py: 1, px: 2 }}
                      >
                        {item.fuelType || "-"}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontSize: "0.875rem", py: 1, px: 2 }}
                      >
                        {item.seats || "-"}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontSize: "0.875rem", py: 1, px: 2 }}
                      >
                        {item.vehicleType || "-"}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.8rem",
                          p: 1,
                          color: item.flag ? "green" : "red",
                          fontWeight: "bold",
                        }}
                      >
                        {item.flag ? "Approved" : "Not Approved"}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={(event) => handleMenuClick(event, item)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              background: "white",
              zIndex: 1,
              border: "none !important",
              borderTop: "none !important",
              borderBottom: "none !important",
            }}
          >
            <CommonTablePagination
              page={page}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              setPage={setPage}
              filteredData={vehicleData}
              styling={{
                display: "flex",
                justifyContent: "flex-end",
                border: "none",
                borderTop: "none",
                borderBottom: "none",
                "& .MuiTablePagination-root": {
                  border: "none",
                  borderTop: "none",
                  borderBottom: "none",
                },
                "& .MuiTablePagination-toolbar": {
                  border: "none",
                  borderTop: "none",
                  borderBottom: "none",
                },
              }}
            />
          </Box>
        </Paper>
      </Box>

      {/* Menu for actions */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        sx={{
          "& .MuiPaper-root": {
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #ccc",
          },
        }}
      >
        <MenuItem onClick={() => handleFuelClick(selectedVehicle)}>
          <ListItemIcon>
            <LocalGasStationIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Fuels</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => handleVerificationDocument(selectedVehicle)}>
          <ListItemIcon>
            <VerifiedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Verification Docs</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => handleApproval(selectedVehicle)}>
          <ListItemIcon>
            {selectedVehicle?.flag ? (
              <CancelIcon fontSize="small" style={{ color: "red" }} />
            ) : (
              <CheckCircleIcon fontSize="small" style={{ color: "green" }} />
            )}
          </ListItemIcon>
          <ListItemText
            primary={selectedVehicle?.flag ? "Reject" : "Approve"}
            primaryTypographyProps={{
              style: {
                color: selectedVehicle?.flag ? "red" : "green",
                fontWeight: "bold",
              },
            }}
          />
        </MenuItem>
      </Menu>

      {isVehicleReportClicked && (
        <>
          <Modal
            setIsExportButtonClicked={setIsVehicleReportClicked}
            isVehicleReport="1"
            type="vehicleReport"
          />
          <div className="add-route-fullcontainer"></div>
        </>
      )}

      {isExportButtonClicked && (
        <>
          <FuelExportComponent
            setIsExportButtonClicked={setIsExportButtonClicked}
            // // regNumber={getVehiclesFromVehicles}
            // isPrivateDriver="1"
            // isFuel="1"
            // type="fuels"
          />
          <div className="add-route-fullcontainer"></div>
        </>
      )}
    </React.Fragment>
  );
};

export default Vehicles;
